/*
 * DTC-1101 — Header first-level navigation overflow controller.
 * Keeps the implementation isolated from theme.js and activates only when the section setting
 * renders <header-nav-scroller> on desktop.
 */
(function () {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 1280px)';
  const EDGE_TOLERANCE = 2;
  const WINDOW_HIDDEN_CLASS = 'is-window-hidden';
  const WINDOW_TRANSITION_CLASS = 'is-window-transitioning';
  const WINDOW_TRANSITION_OUT_MS = 80;
  const WINDOW_STEP = 2;

  class HeaderNavScroller extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-nav-scroller-viewport]');
      this.list = this.viewport && this.viewport.querySelector(':scope > .horizontal-list');
      this.controls = this.querySelector('[data-nav-scroller-controls]');
      this.previousButton = this.querySelector('[data-nav-scroller-previous]');
      this.nextButton = this.querySelector('[data-nav-scroller-next]');
      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
      this.isRtl = this.dataset.rtl === 'true';
      this.startIndex = 0;
      this.visibleEnd = 0;
      this.windowStarts = [0];
      this.windowPosition = 0;
      this.windowStartItem = null;
      this.isSwitching = false;

      if (!this.viewport || !this.list || !this.controls || !this.previousButton || !this.nextButton) return;

      this.handlePrevious = () => this.changeWindow(-1);
      this.handleNext = () => this.changeWindow(1);
      this.handleFocusIn = (event) => this.revealFocusedItem(event);
      this.handleMediaChange = () => this.scheduleRefresh(true);
      this.handleFontsLoaded = () => this.scheduleRefresh(true);

      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.list.addEventListener('focusin', this.handleFocusIn);
      this.mediaQuery.addEventListener('change', this.handleMediaChange);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh(true));
        this.resizeObserver.observe(this.viewport);
      } else {
        this.handleWindowResize = () => this.scheduleRefresh(true);
        window.addEventListener('resize', this.handleWindowResize, { passive: true });
      }

      if (window.MutationObserver) {
        this.mutationObserver = new MutationObserver(() => this.scheduleRefresh(true));
        this.mutationObserver.observe(this.list, { childList: true, characterData: true, subtree: true });
      }

      if (document.fonts) {
        document.fonts.ready.then(this.handleFontsLoaded);
        document.fonts.addEventListener && document.fonts.addEventListener('loadingdone', this.handleFontsLoaded);
      }

      this.scheduleRefresh(true);
    }

    disconnectedCallback() {
      if (!this.viewport) return;

      this.previousButton.removeEventListener('click', this.handlePrevious);
      this.nextButton.removeEventListener('click', this.handleNext);
      this.list.removeEventListener('focusin', this.handleFocusIn);
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      this.resizeObserver && this.resizeObserver.disconnect();
      this.mutationObserver && this.mutationObserver.disconnect();
      this.handleWindowResize && window.removeEventListener('resize', this.handleWindowResize);
      document.fonts && document.fonts.removeEventListener &&
        document.fonts.removeEventListener('loadingdone', this.handleFontsLoaded);
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.transitionFrame);
      clearTimeout(this.transitionTimer);
      this.clearWindowState();
    }

    get items() {
      return Array.from(this.list.children).filter((item) => item.classList.contains('level0'));
    }

    scheduleRefresh(reanchor) {
      this.shouldReanchor = this.shouldReanchor || reanchor;
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => {
        const shouldReanchor = this.shouldReanchor;
        this.shouldReanchor = false;
        this.refresh(shouldReanchor);
      });
    }

    refresh(reanchor) {
      const items = this.items;

      if (!this.mediaQuery.matches) {
        this.controls.hidden = true;
        this.classList.remove('is-ready', 'is-overflowing', 'is-windowing', WINDOW_TRANSITION_CLASS);
        this.clearWindowState(items);
        this.startIndex = 0;
        this.visibleEnd = items.length;
        this.windowStarts = [0];
        this.windowPosition = 0;
        this.windowStartItem = items[0] || null;
        return;
      }

      this.clearWindowState(items);

      if (!items.length) {
        this.controls.hidden = true;
        this.classList.remove('is-overflowing', 'is-windowing');
        this.classList.add('is-ready');
        this.windowStarts = [0];
        this.windowPosition = 0;
        return;
      }

      const viewportRect = this.getVisibleRect();
      const availableWidth = viewportRect.right - viewportRect.left;
      const geometry = this.measureItems(items);
      const isOverflowing = geometry.totalWidth - availableWidth > EDGE_TOLERANCE;

      this.classList.toggle('is-overflowing', isOverflowing);
      this.classList.toggle('is-windowing', isOverflowing);
      this.classList.add('is-ready');
      this.controls.hidden = !isOverflowing;

      if (isOverflowing) {
        const previousStartIndex = this.windowStartItem ? items.indexOf(this.windowStartItem) : -1;
        const desiredStartIndex = previousStartIndex >= 0 ? previousStartIndex : this.startIndex;
        this.windowStarts = this.calculateWindowStarts(availableWidth, geometry);
        this.windowPosition = this.findWindowPosition(desiredStartIndex, this.windowStarts);
        this.startIndex = this.windowStarts[this.windowPosition];

        const focusedItem = document.activeElement && document.activeElement.closest('.level0');
        const focusedIndex = focusedItem && focusedItem.parentElement === this.list
          ? items.indexOf(focusedItem)
          : -1;

        let visibleEnd = this.calculateVisibleEnd(this.startIndex, availableWidth, geometry);
        if (focusedIndex >= 0 && (focusedIndex < this.startIndex || focusedIndex >= visibleEnd)) {
          const focusedWindowPosition = this.windowStarts.findIndex((startIndex) => {
            const endIndex = this.calculateVisibleEnd(startIndex, availableWidth, geometry);
            return focusedIndex >= startIndex && focusedIndex < endIndex;
          });

          this.windowPosition = focusedWindowPosition >= 0 ? focusedWindowPosition : this.windowPosition;
          this.startIndex = this.windowStarts[this.windowPosition];
          visibleEnd = this.calculateVisibleEnd(this.startIndex, availableWidth, geometry);
        }

        this.visibleEnd = visibleEnd;
        this.windowStartItem = items[this.startIndex];
        this.applyWindow(items, this.startIndex, visibleEnd);
        this.previousButton.disabled = this.windowPosition === 0;
        this.nextButton.disabled = this.windowPosition === this.windowStarts.length - 1;
      } else {
        this.startIndex = 0;
        this.visibleEnd = items.length;
        this.windowStarts = [0];
        this.windowPosition = 0;
        this.windowStartItem = items[0];
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
      }

      this.viewport.scrollLeft = 0;
      if (reanchor) this.anchorDropdownPanels();
    }

    getVisibleRect() {
      const viewportRect = this.viewport.getBoundingClientRect();
      const controlsRect = this.controls.hidden ? null : this.controls.getBoundingClientRect();

      return {
        left: controlsRect && this.isRtl ? Math.max(viewportRect.left, controlsRect.right) : viewportRect.left,
        right: controlsRect && !this.isRtl ? Math.min(viewportRect.right, controlsRect.left) : viewportRect.right
      };
    }

    measureItems(items) {
      const rects = items.map((item) => item.getBoundingClientRect());
      const widths = rects.map((rect) => rect.width);
      const gaps = rects.map((rect, index) => {
        if (index === 0) return 0;

        const previousRect = rects[index - 1];
        return Math.max(0, this.isRtl
          ? previousRect.left - rect.right
          : rect.left - previousRect.right);
      });
      const totalWidth = widths.reduce((total, width) => total + width, 0) +
        gaps.reduce((total, gap) => total + gap, 0);

      return { widths, gaps, totalWidth };
    }

    calculateVisibleEnd(startIndex, availableWidth, geometry) {
      let occupiedWidth = 0;
      let visibleEnd = startIndex;

      for (let index = startIndex; index < geometry.widths.length; index += 1) {
        const nextWidth = geometry.widths[index] + (index > startIndex ? geometry.gaps[index] : 0);
        if (index > startIndex && occupiedWidth + nextWidth > availableWidth + EDGE_TOLERANCE) break;

        occupiedWidth += nextWidth;
        visibleEnd = index + 1;

        if (occupiedWidth > availableWidth + EDGE_TOLERANCE) break;
      }

      return visibleEnd;
    }

    calculateWindowStarts(availableWidth, geometry) {
      const starts = [0];
      const itemCount = geometry.widths.length;
      let currentStart = 0;

      while (this.calculateVisibleEnd(currentStart, availableWidth, geometry) < itemCount) {
        let nextStart = Math.min(currentStart + WINDOW_STEP, itemCount - 1);

        for (let candidate = currentStart + 1; candidate < nextStart; candidate += 1) {
          if (this.calculateVisibleEnd(candidate, availableWidth, geometry) >= itemCount) {
            nextStart = candidate;
            break;
          }
        }

        if (nextStart <= currentStart) break;
        starts.push(nextStart);
        currentStart = nextStart;
      }

      return starts;
    }

    findWindowPosition(desiredStartIndex, starts) {
      const exactPosition = starts.indexOf(desiredStartIndex);
      if (exactPosition >= 0) return exactPosition;

      let closestPosition = 0;
      starts.forEach((startIndex, position) => {
        if (Math.abs(startIndex - desiredStartIndex) < Math.abs(starts[closestPosition] - desiredStartIndex)) {
          closestPosition = position;
        }
      });

      return closestPosition;
    }

    applyWindow(items, startIndex, visibleEnd) {
      items.forEach((item, index) => {
        const isHidden = index < startIndex || index >= visibleEnd;
        item.classList.toggle(WINDOW_HIDDEN_CLASS, isHidden);
        item.toggleAttribute('inert', isHidden);

        if (isHidden) {
          item.setAttribute('aria-hidden', 'true');
        } else {
          item.removeAttribute('aria-hidden');
        }
      });
    }

    clearWindowState(items = this.items) {
      items.forEach((item) => {
        item.classList.remove(WINDOW_HIDDEN_CLASS);
        item.removeAttribute('aria-hidden');
        item.removeAttribute('inert');
      });
    }

    changeWindow(delta) {
      if (this.isSwitching || !this.classList.contains('is-overflowing')) return;
      const nextPosition = Math.min(Math.max(this.windowPosition + delta, 0), this.windowStarts.length - 1);
      if (nextPosition === this.windowPosition) return;

      const nextIndex = this.windowStarts[nextPosition];

      this.closeDropdownPanels();
      this.windowPosition = nextPosition;
      this.startIndex = nextIndex;
      this.windowStartItem = this.items[nextIndex];

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.refresh(true);
        return;
      }

      this.isSwitching = true;
      this.setAttribute('aria-busy', 'true');
      this.classList.add(WINDOW_TRANSITION_CLASS);
      this.transitionTimer = setTimeout(() => {
        this.refresh(true);
        this.transitionFrame = requestAnimationFrame(() => {
          this.classList.remove(WINDOW_TRANSITION_CLASS);
          this.removeAttribute('aria-busy');
          this.isSwitching = false;
        });
      }, WINDOW_TRANSITION_OUT_MS);
    }

    closeDropdownPanels() {
      this.items.forEach((item) => {
        item.classList.remove('visible', 'active-submenu');
        item.querySelectorAll('[aria-expanded="true"]').forEach((control) => {
          control.setAttribute('aria-expanded', 'false');
        });
      });
    }

    revealFocusedItem(event) {
      const item = event.target.closest('.level0');
      if (!item || item.parentElement !== this.list) return;

      if (item.classList.contains(WINDOW_HIDDEN_CLASS)) {
        this.startIndex = this.items.indexOf(item);
        this.windowStartItem = item;
      }

      this.scheduleRefresh(true);
    }

    anchorDropdownPanels() {
      const header = this.closest('header');
      if (!header) return;

      const headerRect = header.getBoundingClientRect();
      this.items
        .filter((item) => !item.classList.contains(WINDOW_HIDDEN_CLASS))
        .filter((item) => item.classList.contains('layout-mode-relative'))
        .forEach((item) => {
          const itemRect = item.getBoundingClientRect();
          const submenu = item.querySelector(':scope > .submenu');
          if (!submenu) return;

          const halfWidth = submenu.offsetWidth / 2;
          const minimum = halfWidth + 8;
          const maximum = headerRect.width - halfWidth - 8;
          const itemCenter = itemRect.left - headerRect.left + itemRect.width / 2;
          const anchor = minimum <= maximum
            ? Math.min(Math.max(itemCenter, minimum), maximum)
            : headerRect.width / 2;

          item.style.setProperty('--nav-dropdown-anchor', `${anchor}px`);
        });

      this.items
        .filter((item) => !item.classList.contains(WINDOW_HIDDEN_CLASS))
        .filter((item) => item.classList.contains('layout-mode-fullwidth') || item.classList.contains('layout-mode-legacy'))
        .forEach((item) => {
          const submenu = item.querySelector(':scope > .submenu');
          if (!submenu) return;

          const currentShift = Number.parseFloat(item.style.getPropertyValue('--nav-panel-shift')) || 0;
          const shift = currentShift + headerRect.left - submenu.getBoundingClientRect().left;
          item.style.setProperty('--nav-panel-shift', `${shift}px`);
        });
    }
  }

  if (!customElements.get('header-nav-scroller')) {
    customElements.define('header-nav-scroller', HeaderNavScroller);
  }
})();
